import type { UserRole } from '@/types/database';
import { FRONTEND_BUSINESS_RULES } from '@/config/appConfig';

export const ROLES = {
  SUPERVISOR: 'supervisor',
  ADMIN: 'admin',
  TEACHER: 'teacher',
  KIOSK: 'kiosk',
  TREASURY: 'treasury',
  GENERAL_AFFAIRS: 'general_affairs',
  STAFF: 'staff',
  // Phase 2 (排班) — staff-equivalent access (spec §2.1).
  LIFEGUARD: 'lifeguard',
  WORK_STUDY: 'work_study',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const PERMISSIONS = {
  // Supervisor: Can do everything
  [ROLES.SUPERVISOR]: {
    canViewSystemManagement: true,
    canViewAdminSection: true,
    // 課務總覽 panel (ActiveStatusPanelModal — surfaces 待確認 queue data). Distinct
    // from canViewAdminSection, which kiosk also holds only to show the iPad 簽到 nav
    // link. supervisor + admin only. (2026-07-17 P1-F1 kiosk gap.)
    canViewCourseOpsPanel: true,
    canViewTeacherSection: true,
    canApproveTeacherNotes: true,
    canBatchApproveClasses: false,
    canEditFeedback: true,
    canViewFeedbackQueue: true,
    canViewApprovalHistory: true,
    canAccessAll: true,
    canViewKiosk: true,
    // Payment module
    canViewPaymentModule: true,
    canViewApprovalQueue: true,
    canViewTreasuryDashboard: true,
    canManagePayees: true,
    canManagePaymentSettings: true,
    canManageTravelRateTables: true,

    // 紙本憑證頁（原「發票收驗」）。一般那一包的已收／追蹤：總務**或主管**
    // （PO 2026-09-13 修訂 —— 已收一律要過三項核對清單，不會被誤按）；
    // 延長／結案與機密那一包的全部動作（收件、追蹤、月結）：主管。出納唯讀。
    canViewInvoiceVerification: true,
    canCreateSupervisorPayment: true,
    canCreateRefundRequest: true,
    // Employee management
    canManageEmployees: true,
    canEditEmployeeBankDetails: true,
    canViewEmployees: true,
    // Staff account management
    canManageAccounts: true,
    canDeactivateStaff: true,
    canLinkAccounts: true,
    // Attendance module
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: true,
    canExportAttendance: true,
    // Scheduling (排班) — manage roster (admin+supervisor); configure visibility (supervisor only)
    canManageScheduling: true,
    canManageScheduleVisibility: true,
    // HR 設定 (三節獎金 etc.) — supervisor only
    canManageHrConfig: true,
    canManageSignupPricing: true,
    // 招生訊息範本預設文案 (PO 2026-09-18) — supervisor only, same gate class as
    // pricing: a wrong default reaches every future parent and nobody notices.
    canManageSignupTemplates: true,
    // 班別識別統一 (class-identity canonicalization) — supervisor only
    canManageClassIdentity: true,
    // 常態班更名 DT9 — the monthly 「BF 常態班：要改班名、換老師？」 banner: supervisor + admin
    // (課務), one version for both roles (PO 2026-10-04).
    seesBfChangeReminder: true,
    // 我的請款 menu / home tile. False only for kiosk (a device, not a person).
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
  // Admin: Limited access
  [ROLES.ADMIN]: {
    canViewSystemManagement: false,
    canViewAdminSection: true,
    // 課務總覽 panel — admin manages the 待確認 queue. (2026-07-17 P1-F1.)
    canViewCourseOpsPanel: true,
    canViewTeacherSection: true,
    canApproveTeacherNotes: false, // Cannot approve or edit teacher notes
    canBatchApproveClasses: false, // Cannot batch approve
    canEditFeedback: true,
    canViewFeedbackQueue: true,
    canViewApprovalHistory: true, // Can view history
    canAccessAll: false,
    canViewKiosk: true,
    // Payment module
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,

    canViewInvoiceVerification: false,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: true,
    // Employee management
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    // Staff account management
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    // Attendance module
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: false,
    // Scheduling (排班) — supervisor-only for whole-roster management (spec §2.3, 2026-07-06).
    // Admin can still self-schedule their OWN shifts via a per-employee toggle (backend-gated),
    // but is NO LONGER a roster manager and cannot configure visibility grants.
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    // 課務 cannot open 常態班更名 but sees its monthly BF reminder banner (DT9).
    seesBfChangeReminder: true,
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
  // Teacher: Very limited access
  [ROLES.TEACHER]: {
    canViewSystemManagement: false,
    canViewAdminSection: false,
    canViewCourseOpsPanel: false,
    canViewTeacherSection: true,
    canApproveTeacherNotes: true,
    canBatchApproveClasses: true,
    canEditFeedback: false, // Cannot use the '操作' column in FeedbackPage
    canViewFeedbackQueue: false,
    canViewApprovalHistory: true,
    canAccessAll: false,
    canViewKiosk: false,
    // Payment module
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,

    canViewInvoiceVerification: false,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: false,
    // Employee management
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    // Staff account management
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    // Attendance module
    // Teacher: punch nav/route surface enabled; actual 打卡 eligibility is
    // decided server-side by the per-employee attendanceEnabled toggle
    // (PO 2026-06-11 — gating is per-account, not per-role). KEEPS 請假/我的出勤.
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: false,
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
  // Kiosk: Limited access - Kiosk page, Tools, and Help only
  [ROLES.KIOSK]: {
    canViewSystemManagement: false,
    canViewAdminSection: true, // Limited: Only shows iPad 簽到 link
    // Kiosk must NOT see the 課務總覽 panel (待確認 queue) — it only holds
    // canViewAdminSection for the nav 簽到 link. (2026-07-17 P1-F1 fix.)
    canViewCourseOpsPanel: false,
    canViewTeacherSection: false,
    canApproveTeacherNotes: false,
    canBatchApproveClasses: false,
    canEditFeedback: false,
    canViewFeedbackQueue: false,
    canViewApprovalHistory: false,
    canAccessAll: false,
    canViewKiosk: true,
    // Payment module
    canViewPaymentModule: false,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,

    canViewInvoiceVerification: false,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: false,
    // Employee management
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    // Staff account management
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    // Attendance module
    canPunchAttendance: false,
    canRequestLeave: false,
    canReviewAttendance: false,
    canExportAttendance: false,
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    canViewMyPaymentRequests: false,
    canViewClassRosterMobile: false,
  },
  // Treasury: Payment module only
  [ROLES.TREASURY]: {
    canViewSystemManagement: false,
    canViewAdminSection: false,
    canViewCourseOpsPanel: false,
    canViewTeacherSection: false,
    canApproveTeacherNotes: false,
    canBatchApproveClasses: false,
    canEditFeedback: false,
    canViewFeedbackQueue: false,
    canViewApprovalHistory: false,
    canAccessAll: false,
    canViewKiosk: false,
    // Payment module
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: true,
    canManagePayees: true,
    canManagePaymentSettings: false, // supervisor only
    // 日支數額表 (出差旅費 per-diem rate tables) — 出納 maintains it alongside 主管
    // (PO 2026-09-03). Deliberately NOT canManagePaymentSettings, which stays
    // supervisor-only and also gates 付款設定 / GSheets 待辦 / ConfigPage sections.
    // Segregation-of-duties note in docs/AUDIT_ACCEPTED_RISKS.md (Travel T18).
    canManageTravelRateTables: true,

    // 紙本憑證頁：出納唯讀（看得到紙本進度，沒有任何動作按鈕）。影像的「已驗證」
    // 仍在出納作業頁，與紙本保管是兩條線 —— 付款不等紙本。
    canViewInvoiceVerification: true,
    canCreateSupervisorPayment: true,
    canCreateRefundRequest: false,
    // Employee management
    canManageEmployees: false,
    canEditEmployeeBankDetails: true,
    canViewEmployees: true,
    // Staff account management
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    // Attendance module
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: true,
    // Treasury exports attendance but does NOT manage the roster (spec §2.3 — false on both).
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    // 薪酬福利設定 (HR config) is supervisor-only (spec §2.3, 2026-07-06). Treasury loses
    // this menu/page: the holiday-driven OT day-typing its export needs is computed
    // server-side, so treasury loses nothing operationally.
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
  // General Affairs (總務): Invoice verification only
  [ROLES.GENERAL_AFFAIRS]: {
    canViewSystemManagement: false,
    canViewAdminSection: false,
    canViewCourseOpsPanel: false,
    canViewTeacherSection: false,
    canApproveTeacherNotes: false,
    canBatchApproveClasses: false,
    canEditFeedback: false,
    canViewFeedbackQueue: false,
    canViewApprovalHistory: false,
    canAccessAll: false,
    canViewKiosk: false,
    // Payment module — invoice verification only
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,

    canViewInvoiceVerification: true,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: false,
    // Employee management
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    // Staff account management
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    // Attendance module
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: false,
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
  // Staff (一般員工): Payment module only — my requests + payslips
  [ROLES.STAFF]: {
    canViewSystemManagement: false,
    canViewAdminSection: false,
    canViewCourseOpsPanel: false,
    canViewTeacherSection: false,
    canApproveTeacherNotes: false,
    canBatchApproveClasses: false,
    canEditFeedback: false,
    canViewFeedbackQueue: false,
    canViewApprovalHistory: false,
    canAccessAll: false,
    canViewKiosk: false,
    // Payment module — my requests + payslips only
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,

    canViewInvoiceVerification: false,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: false,
    // Employee management
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    // Staff account management
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    // Attendance module
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: false,
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
  // 救生員 (lifeguard): identical access to 一般員工 (staff) — Phase 2 spec §2.1.
  [ROLES.LIFEGUARD]: {
    canViewSystemManagement: false,
    canViewAdminSection: false,
    canViewCourseOpsPanel: false,
    canViewTeacherSection: false,
    canApproveTeacherNotes: false,
    canBatchApproveClasses: false,
    canEditFeedback: false,
    canViewFeedbackQueue: false,
    canViewApprovalHistory: false,
    canAccessAll: false,
    canViewKiosk: false,
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,
    canViewInvoiceVerification: false,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: false,
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: false,
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    // 救生員 file 請款 like 一般員工 (PO 2026-09-16, reversing the earlier
    // decision): the menu entry, the phone tile and REQUEST_CREATOR_ROLES on both
    // tiers all had to move together — a flag on its own only opens a dead page.
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: true,
  },
  // 工讀生 (work-study): identical access to 一般員工 (staff) — Phase 2 spec §2.1.
  [ROLES.WORK_STUDY]: {
    canViewSystemManagement: false,
    canViewAdminSection: false,
    canViewCourseOpsPanel: false,
    canViewTeacherSection: false,
    canApproveTeacherNotes: false,
    canBatchApproveClasses: false,
    canEditFeedback: false,
    canViewFeedbackQueue: false,
    canViewApprovalHistory: false,
    canAccessAll: false,
    canViewKiosk: false,
    canViewPaymentModule: true,
    canViewApprovalQueue: false,
    canViewTreasuryDashboard: false,
    canManagePayees: false,
    canManagePaymentSettings: false,
    canManageTravelRateTables: false,
    canViewInvoiceVerification: false,
    canCreateSupervisorPayment: false,
    canCreateRefundRequest: false,
    canManageEmployees: false,
    canEditEmployeeBankDetails: false,
    canViewEmployees: false,
    canManageAccounts: false,
    canDeactivateStaff: false,
    canLinkAccounts: false,
    canPunchAttendance: true,
    canRequestLeave: true,
    canReviewAttendance: false,
    canExportAttendance: false,
    canManageScheduling: false,
    canManageScheduleVisibility: false,
    canManageHrConfig: false,
    canManageSignupPricing: false,
    canManageSignupTemplates: false,
    canManageClassIdentity: false,
    seesBfChangeReminder: false,
    canViewMyPaymentRequests: true,
    canViewClassRosterMobile: false,
  },
};

// ─── 救生員 手機版 (docs/LIFEGUARD_MOBILE_ACCESS_SPEC.md) ─────────────────────
// ONE rule on both tiers: view-only roster = holds a role in ROSTER_VIEW_ONLY_ROLES
// and NONE of ROSTER_FULL_ACCESS_ROLES. The backend decides the same way from
// server-read roles (functions utils/classRosterAccess.ts isRosterViewOnlyCaller);
// the lists are mirrored and parity-tested. Everything below is layered on it.

const hasAnyRole = (roles: readonly string[], wanted: readonly string[]): boolean =>
  wanted.some((role) => roles.includes(role));

/** View-only roster user (lifeguard without teacher / admin / supervisor). */
export function isRosterViewOnly(roles: readonly string[] | null | undefined): boolean {
  const list = roles ?? [];
  const TM = FRONTEND_BUSINESS_RULES.TEACHER_MOBILE;
  return (
    hasAnyRole(list, TM.ROSTER_VIEW_ONLY_ROLES) && !hasAnyRole(list, TM.ROSTER_FULL_ACCESS_ROLES)
  );
}

/**
 * 救生員模式 ↔ 老師模式 switch (PO 2026-09-16): a teacher who also holds a view-only
 * roster role may view the teacher mobile pages exactly as a 救生員 (all classes of
 * today…+6, read-only). The backend honours it only for holders of the role
 * (functions classRosterAccess isViewOnlyRequest).
 */
export function canSwitchRosterMode(roles: readonly string[] | null | undefined): boolean {
  const list = roles ?? [];
  return (
    list.includes(ROLES.TEACHER) &&
    hasAnyRole(list, FRONTEND_BUSINESS_RULES.TEACHER_MOBILE.ROSTER_VIEW_ONLY_ROLES)
  );
}

/** Roles whose own landing page outranks the lifeguard home (出納 / 總務 / iPad). */
const OWN_LANDING_ROLES: readonly string[] = [ROLES.TREASURY, ROLES.GENERAL_AFFAIRS, ROLES.KIOSK];

/**
 * Lands on /teacher/home and uses the phone-first 上課名單 home. lifeguard+出納 /
 * lifeguard+總務 keep /home (their work is there) and reach the roster from the
 * menu (canViewClassRosterMobile).
 */
export function isLifeguardMobileUser(roles: readonly string[] | null | undefined): boolean {
  const list = roles ?? [];
  return isRosterViewOnly(list) && !hasAnyRole(list, OWN_LANDING_ROLES);
}

/** Where /login (and /) sends a signed-in user when there is no ?redirect= target. */
export function resolveLandingRoute(roles: readonly string[] | null | undefined): string {
  const list = roles ?? [];
  // Kiosk-only accounts go straight to the kiosk.
  if (list.includes(ROLES.KIOSK) && list.length === 1) return '/kiosk';
  // Teachers without admin/supervisor go to the teacher home.
  if (list.includes(ROLES.TEACHER) && !hasAnyRole(list, [ROLES.ADMIN, ROLES.SUPERVISOR])) {
    return '/teacher/home';
  }
  if (isLifeguardMobileUser(list)) return '/teacher/home';
  return '/home';
}

export const DEFAULT_ROUTES: Record<UserRole, string> = {
  [ROLES.SUPERVISOR]: '/admin/schedule',
  [ROLES.ADMIN]: '/admin/schedule',
  [ROLES.TEACHER]: '/teacher/mobile',
  [ROLES.KIOSK]: '/kiosk',
  [ROLES.TREASURY]: '/payment/requests',
  [ROLES.GENERAL_AFFAIRS]: '/payment/paper-receipts',
  [ROLES.STAFF]: '/payment/payslips',
  // Staff-equivalent landing (Phase 2 roles).
  [ROLES.LIFEGUARD]: '/payment/payslips',
  [ROLES.WORK_STUDY]: '/payment/payslips',
};
